import React, { useState } from "react";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button as AntdButton, Form, Input, Modal, Space, message } from "antd";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import Table, { TableColumn } from "../../../../../components/Table";
import { Button, TableContainer } from "../../../styles";
import CreditorsService from "../../service/creditorsService";
import { Creditor, CreditorTableProps } from "../../type";
import { formatDocument } from "../../../../../utils/generalFormat";
import { can, PortalPermissions } from "../../../../../config/permissions";

export const CreditorsTable: React.FC<CreditorTableProps> = ({
  data,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRefresh,
}) => {
  const creditorsService = new CreditorsService();
  const [isLoading, setIsLoading] = useState(false);

  // Modal Criar
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();

  // Modal Editar
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCreditor, setSelectedCreditor] = useState<Creditor | null>(
    null,
  );
  const [editForm] = Form.useForm();

  const handleOpenCreateModal = () => {
    createForm.resetFields();
    setCreateModalOpen(true);
  };

  const handleOpenEditModal = (record: Creditor) => {
    setSelectedCreditor(record);
    editForm.setFieldsValue({
      document: formatDocument(record.document),
      holderName: record.holderName,
    });
    setEditModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    createForm.resetFields();
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedCreditor(null);
    editForm.resetFields();
  };

  const applyDocumentMask = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 14);

    if (digits.length <= 11) {
      // CPF: 000.000.000-00
      return digits
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    // CNPJ: 00.000.000/0000-00
    return digits
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDocumentMask(e.target.value);
    createForm.setFieldValue("document", masked);
  };

  const handleCreate = async (values: {
    document: string;
    holderName: string;
  }) => {
    try {
      setIsLoading(true);
      await creditorsService.createCreditor({
        document: values.document.replace(/\D/g, ""),
        holderName: values.holderName,
      });
      message.success("Credor cadastrado com sucesso!");
      handleCloseCreateModal();
      onRefresh();
    } catch (error: any) {
      message.error(error?.message || "Erro ao cadastrar credor.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (values: { holderName: string }) => {
    if (!selectedCreditor) return;
    try {
      setIsLoading(true);
      await creditorsService.updateCreditor(selectedCreditor.id, {
        holderName: values.holderName,
      });
      message.success("Credor atualizado com sucesso!");
      handleCloseEditModal();
      onRefresh();
    } catch (error: any) {
      message.error(error?.message || "Erro ao atualizar credor.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <LoadingOverlay show={isLoading} relative />

      <TableContainer>
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          {can(PortalPermissions.contratos.cadastroCredores.criar) && (
            <Button
              icon={<PlusOutlined />}
              onClick={handleOpenCreateModal}
              style={{
                backgroundColor: "#CF9B51",
                color: "white",
                border: "none",
                width: "auto",
              }}
            >
              Novo Credor
            </Button>
          )}
        </div>

        <Table
          dataSource={data.records}
          pagination={{
            defaultPageSize: pageSize,
            defaultCurrent: currentPage,
            current: currentPage,
            total: data.count,
            onChange: (page) => onPageChange(page),
            onShowSizeChange: (_, size) => onPageSizeChange(size),
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} credores`,
          }}
        >
          <TableColumn
            title="Documento"
            dataIndex="document"
            key="document"
            render={(doc: string) => formatDocument(doc)}
          />
          <TableColumn
            title="Nome do Titular"
            dataIndex="holderName"
            key="holderName"
          />
          <TableColumn
            title="Ações"
            key="actions"
            width={80}
            align="center"
            render={(_: any, record: any) => (
              <AntdButton
                type="text"
                icon={
                  <EditOutlined
                    style={{ color: "#CF9B51", fontSize: "18px" }}
                  />
                }
                onClick={() => handleOpenEditModal(record as Creditor)}
              />
            )}
          />
        </Table>
      </TableContainer>

      {/* ── Modal: Cadastrar Credor ── */}
      <Modal
        title="Cadastrar Credor"
        open={createModalOpen}
        onCancel={handleCloseCreateModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          style={{ marginTop: "16px" }}
        >
          <Form.Item
            label="Documento"
            name="document"
            rules={[
              { required: true, message: "Informe o documento." },
              {
                pattern: /^[\d.\-/]+$/,
                message: "Documento inválido. Use apenas CPF ou CNPJ.",
              },
            ]}
          >
            <Input placeholder="CPF ou CNPJ" onChange={handleDocumentChange} />
          </Form.Item>

          <Form.Item
            label="Nome do Titular"
            name="holderName"
            rules={[{ required: true, message: "Informe o nome do titular." }]}
          >
            <Input placeholder="Nome completo" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: "24px" }}>
            <Space style={{ float: "right" }}>
              <AntdButton onClick={handleCloseCreateModal}>Cancelar</AntdButton>
              <AntdButton
                type="primary"
                htmlType="submit"
                style={{ backgroundColor: "#062D2C", border: "none" }}
              >
                Salvar
              </AntdButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: Editar Credor ── */}
      <Modal
        title="Editar Credor"
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
          style={{ marginTop: "16px" }}
        >
          <Form.Item label="Documento" name="document">
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="Nome do Titular"
            name="holderName"
            rules={[{ required: true, message: "Informe o nome do titular." }]}
          >
            <Input placeholder="Nome completo" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: "24px" }}>
            <Space style={{ float: "right" }}>
              <AntdButton onClick={handleCloseEditModal}>Cancelar</AntdButton>
              <AntdButton
                type="primary"
                htmlType="submit"
                style={{ backgroundColor: "#062D2C", border: "none" }}
              >
                Atualizar
              </AntdButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
